import React, { Fragment, useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styles from './styles.scss';
import classnames from 'classnames';
import densityMarkRect from '../../assets/images/density-mark-rect.svg';
import FormLabel from '../FormLabel';
import SearchableSelectBox from '../SearchableSelectBox';

import SessionStore from '../../rx-stores/session';
import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import { useRequiredSessionToken } from '../../utils/session-token';
import { ON_PREM } from '../../utils/environment';

import {
  Resource,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import { Organization } from '../../types/api';
import { OrganizationIdOrAll } from '../../types/all-organizations';
import { SenseiApi } from '../../utils/api';

import {
  AppBar,
  AppBarTitle,
  AppBarContext,
  AppBarSection,
  Button,
  Modal,
  RadioButton,
  InputBox,
  Icons,
  DensityMark,
  Skeleton,
  Checkbox,
} from '@densityco/ui';
import colorVariables from '@densityco/ui/variables/colors.json';
import HorizontalForm from '../HorizontalForm';

import {
  Environment,
  EnvironmentConfiguration,
  EnvironmentConfigurationLocal,
  EnvironmentConfigurationCustom,
  EnvironmentActionTypes,
} from '../../types/environment';
import { ALL_ORGANIZATIONS } from '../../types/all-organizations';
import EnvironmentStore, {
  DEFAULT_LOCAL_SENSEI_API_PORT,
  DEFAULT_CUSTOM_SETTINGS,
  IS_SENSEI_API_DEV_ENVIRONMENT_LOCKED,
} from '../../rx-stores/environment';

const BLUE = colorVariables.midnight,
  RED = colorVariables.red,
  YELLOW = colorVariables.yellow,
  GREEN = colorVariables.green;

const ENVIRONMENTS: Array<{
  type:
    | Environment.PRODUCTION
    | Environment.STAGING
    | Environment.LOCAL
    | Environment.CUSTOM;
  name: string;
  description: string;
  color: string;
  controls: (
    conf: EnvironmentConfiguration,
    setConf: any,
  ) => React.ReactNode | null;
  defaultValue: EnvironmentConfiguration | null;
}> = (() => {
  let production = {
    type: Environment.PRODUCTION,
    name: 'Production',
    description: 'The environment containing real, up to date customer data.',
    color: undefined,
    controls: null,
    defaultValue: null,
  };

  let staging = {
    type: Environment.STAGING,
    name: 'Staging / Lab',
    description:
      'The environment containing mock data used primarily by developers for testing.',
    color: RED,
    controls: null,
    defaultValue: null,
  };

  let local = {
    type: Environment.LOCAL,
    name: 'Local',
    description: 'An instance of the sensei api running on localhost.',
    color: YELLOW,
    controls: (
      selectedEnvironment: EnvironmentConfigurationLocal,
      setSelectedEnvironment,
    ) => (
      <FormLabel
        label="Sensei API Port:"
        htmlFor="sensei-port"
        input={
          <InputBox
            placeholder="Sensei API Port"
            id="sensei-port"
            value={selectedEnvironment.port}
            width="100%"
            onChange={e =>
              setSelectedEnvironment({
                ...selectedEnvironment,
                port: e.target.value,
              })
            }
          />
        }
      />
    ),
    defaultValue: {
      type: Environment.LOCAL,
      port: DEFAULT_LOCAL_SENSEI_API_PORT.toString(),
    },
  };

  let custom = {
    type: Environment.CUSTOM,
    name: 'Custom',
    description: 'Unusual local setup, a custom dev cluster, etc',
    color: BLUE,
    controls: (
      selectedEnvironment: EnvironmentConfigurationCustom,
      setSelectedEnvironment,
    ) => (
      <Fragment>
        <FormLabel
          label="Sensei API Base Url"
          htmlFor="sensei"
          input={
            <InputBox
              value={selectedEnvironment.senseiApiBaseUrl}
              id="sensei"
              width="100%"
              onChange={e =>
                setSelectedEnvironment({
                  ...selectedEnvironment,
                  senseiApiBaseUrl: e.target.value,
                })
              }
            />
          }
        />
        <FormLabel
          label="Core Cucumber Base Url"
          htmlFor="cucumber"
          input={
            <InputBox
              value={selectedEnvironment.coreCucumberBaseUrl}
              id="cucumber"
              width="100%"
              onChange={e =>
                setSelectedEnvironment({
                  ...selectedEnvironment,
                  coreCucumberBaseUrl: e.target.value,
                })
              }
            />
          }
        />
      </Fragment>
    ),
    defaultValue: DEFAULT_CUSTOM_SETTINGS,
  };

  return [production, staging, local, custom].filter(env => {
    if (ON_PREM) {
      return env.type !== Environment.STAGING;
    } else {
      return true;
    }
  });
})();

const EnvironmentSwitcherModal: React.FunctionComponent<{}> = () => {
  const [open, setOpen] = useState(false);

  const state = useRxStore(EnvironmentStore);
  const dispatch = useRxDispatch();

  // TODO: This is a hack that needs to be upstreamed into density ui in order
  // to properly let the modal open and close without the animation behavior
  // acting weirdly.
  const [openModal, setOpenModal] = useState(true);
  const [renderModal, setRenderModal] = useState(false);
  useEffect(() => {
    let unmounted = false;
    if (open) {
      setRenderModal(true);
      setOpenModal(true);
    } else {
      setOpenModal(false);
      setTimeout(() => {
        if (!unmounted) {
          setRenderModal(false);
        }
      }, 500);
    }

    return () => {
      unmounted = true;
    };
  }, [open]);

  const configurationForSelectedEnvironment = ENVIRONMENTS.find(
    conf => conf.type === state.configuration.type,
  );
  const button = (
    <button
      className={classnames(styles.environment, {
        [styles.nonProductionEnvironment]:
          state.configuration.type !== Environment.PRODUCTION,
      })}
      style={{ backgroundColor: configurationForSelectedEnvironment.color }}
      onClick={() => setOpen(true)}
      disabled={IS_SENSEI_API_DEV_ENVIRONMENT_LOCKED}
      title={
        IS_SENSEI_API_DEV_ENVIRONMENT_LOCKED
          ? 'The API environment was selected by the frontend startup command.'
          : undefined
      }
      // Since this is a developer feature, don't make it accessible by tab navigation
      // for regular users.
      tabIndex={-1}
    >
      {configurationForSelectedEnvironment.name}
    </button>
  );

  if (!renderModal) {
    return button;
  }

  const close = () => {
    dispatch({ type: EnvironmentActionTypes.ENVIRONMENT_RESET_CONFIGURATION });
    setOpen(false);
  };

  return (
    <Fragment>
      {button}
      <Modal visible={openModal} onEscape={close} onBlur={close} width={500}>
        <AppBar>
          <AppBarTitle>Switch Environment</AppBarTitle>
        </AppBar>
        <p className={styles.environmentDescription}>
          This is a function intended for developers!&nbsp;
          <strong style={{ color: colorVariables.red }}>
            If you opened this by mistake, click cancel to return to Sensei.
          </strong>
        </p>
        <ul className={styles.environmentChoiceList}>
          {ENVIRONMENTS.map(environment => (
            <div
              className={styles.environmentChoiceWrapper}
              key={environment.type}
            >
              <li
                className={classnames(styles.environmentChoice, {
                  [styles.selected]:
                    state.workingConfiguration.type === environment.type,
                })}
                onClick={() =>
                  dispatch({
                    type: EnvironmentActionTypes.ENVIRONMENT_SET_CONFIGURATION,
                    configuration:
                      environment.defaultValue !== null
                        ? environment.defaultValue
                        : { type: environment.type as any },
                  })
                }
              >
                <RadioButton
                  checked={state.workingConfiguration.type === environment.type}
                  onChange={() => {}}
                />
                <div>
                  <h3>{environment.name}</h3>
                  <p>{environment.description}</p>
                </div>
              </li>
              {state.workingConfiguration.type === environment.type &&
              environment.controls !== null ? (
                <div className={styles.environmentChoiceControls}>
                  {environment.controls(
                    state.workingConfiguration,
                    configuration =>
                      dispatch({
                        type:
                          EnvironmentActionTypes.ENVIRONMENT_SET_CONFIGURATION,
                        configuration,
                      }),
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </ul>

        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection>
              <Button
                variant="underline"
                type="muted"
                onClick={() => {
                  window.localStorage.removeItem('environment');
                  // Refresh to reload the app with the new hosts
                  // Delay is here to give feedback that it worked by letting the modal close
                  setTimeout(() => {
                    window.location.reload();
                  }, 100);
                }}
              >
                Reset to default
              </Button>
            </AppBarSection>
            <AppBarSection>
              <HorizontalForm>
                <Button variant="underline" onClick={close}>
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  onClick={() => {
                    dispatch({
                      type:
                        EnvironmentActionTypes.ENVIRONMENT_COMMIT_CONFIGURATION,
                    });
                    setOpen(false);
                    // Refresh to reload the app with the new hosts
                    // Delay is here to give feedback that it worked by letting the modal close
                    setTimeout(() => {
                      window.location.reload();
                    }, 100);
                  }}
                >
                  Save
                </Button>
              </HorizontalForm>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </Modal>
    </Fragment>
  );
};

type IconComponentProps = {
  width?: React.ReactText;
  height?: React.ReactText;
  color?: string;
};

const Navbar: React.FunctionComponent = () => {
  const sessionToken = useRequiredSessionToken(); /* Note: user has to be logged in to show navbar */
  const routerLocation = useLocation();

  // NOTE: this type is a bit unusual! This value can either be:
  // 1. An organization id
  // 2. The literal string "all" (ALL_ORGANIZATIONS)
  // 3. The value `undefined`, which means the page doesn't have an organization prefix in the url
  const { organizationIdOrAll: organizationIdOrAllOrUndefined } = useParams();

  // Load a list of organizations to use when displaying the navbar
  const [organizationList, setOrganizationList] = useState<
    Resource<Array<Organization>>
  >(RESOURCE_IDLE);
  useEffect(() => {
    let mounted = true;

    setOrganizationList(RESOURCE_LOADING);
    SenseiApi.organizationList(sessionToken)
      .then(organizations => {
        if (!mounted) {
          return;
        }
        setOrganizationList({
          status: ResourceStatus.COMPLETE,
          data: organizations,
        });
      })
      .catch(error => {
        if (!mounted) {
          return;
        }
        setOrganizationList({ status: ResourceStatus.ERROR, error });
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Where should clicking on the organization selector go?
  const calculateNextPath = (newOrganizationId: OrganizationIdOrAll): string => {
    // Remove any trailing slashes from path
    const normalizedPath = routerLocation.pathname.replace(/\/*$/, '');

    if (normalizedPath.endsWith('in-configuration')) {
      return `#/${newOrganizationId}/in-configuration${routerLocation.search}`;
    }
    if (normalizedPath.endsWith('implemented')) {
      return `#/${newOrganizationId}/implemented${routerLocation.search}`;
    }
    if (normalizedPath.endsWith('multi-point-installations')) {
      return `#/${newOrganizationId}/multi-point-installations${routerLocation.search}`;
    }

    // If nothing else matches, go here.
    // Note this doesn't include the search parameters since the user may be on another page.
    return `#/${newOrganizationId}/in-configuration`;
  };

  return (
    <AppBar>
      <AppBarSection>
        <a
          className={classnames(styles.mark, {
            [styles.active]: routerLocation.pathname === '/',
          })}
          href="#/"
        >
          <DensityMark size={26} color="white" />
        </a>

        <div
          style={{ marginLeft: 24 }}
          data-test-id="navbar-organization-switcher-wrapper"
        >
          {organizationIdOrAllOrUndefined ? (
            organizationList.status === ResourceStatus.COMPLETE ? (
              <Fragment>
                {organizationList.data.length === 0 ? (
                  <span className={styles.noOrganizationsLoaded}>
                    No organizations found.
                  </span>
                ) : null}
                {organizationList.data.length > 1 ? (
                  <SearchableSelectBox
                    placeholder="Search by Name or ID"
                    choices={[
                      ...organizationList.data.map(o => ({
                        id: o.id,
                        label: o.name,
                        metric: o.number_of_units,
                        href: calculateNextPath(o.id),
                      }))
                    ]}
                    selectedId={organizationIdOrAllOrUndefined}
                    target={selectedChoice => (
                      <div
                        className={styles.navbarSelectBox}
                        data-test-id="navbar-organization-switcher"
                      >
                        <Icons.Building
                          width={16}
                          height={16}
                          color="currentColor"
                        />
                        <span className={styles.text}>
                          {selectedChoice ? (
                            selectedChoice.label
                          ) : (
                            <Fragment>&mdash;</Fragment>
                          )}
                        </span>
                        <span style={{ transform: 'translate(0px, 2px)' }}>
                          <Icons.ChevronDown color="currentColor" />
                        </span>
                      </div>
                    )}
                  />
                ) : null}
              </Fragment>
            ) : (
              <Skeleton height={20} width={200} />
            )
          ) : null}
        </div>
      </AppBarSection>
      <AppBarSection>
        <EnvironmentSwitcherModal />
        <a
          className={classnames(styles.search, {
            [styles.active]: routerLocation.pathname.startsWith('/search'),
          })}
          href="#/search"
        >
          <Icons.Search color="currentColor" />
        </a>
        <a className={styles.logout} href="#/logout">
          Logout
        </a>
      </AppBarSection>
    </AppBar>
  );
};
export default Navbar;
